/**
 * Renders an interstitial page before allowing the user to navigate to the
 * redirect URL.
 */
import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import {compose} from "mu-lambda";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import Link from "@khanacademy/wonder-blocks-link";

import PageRoot from "#app-shell-package/page-root.tsx";
import EmbeddedPageTemplate from "#app-shell-package/page-templates/embedded-page-template.tsx";
import withLocation, {
    type WithLocationProps,
    type WithoutLocation,
} from "#data-access/with-location.tsx";

import {withStrings} from "./locales/link-redirector/load-strings.tsx";

type Props = WithLocationProps;

class LinkRedirector extends React.Component<Props> {
    maybeMalicious: (url: string) => boolean = (url: string) => {
        const canonicalizedUrl = url.trim().toLowerCase();

        // Our main goal here is to prevent `javascript:` or `data:` URLs that
        // could be outright malicious, but our Guardians have never
        // encountered a legit use case for more borderline URLs like
        // `chrome://` or `maps:`, so we'll just allow standard http(s) links.
        return !(
            canonicalizedUrl.startsWith("https://") ||
            canonicalizedUrl.startsWith("http://")
        );
    };

    render(): React.ReactNode {
        const {
            location: {
                queryParams: {url},
            },
        } = this.props;
        const decodedUrl = decodeURIComponent(url);

        return this.maybeMalicious(decodedUrl) ? (
            // We don't really want to send the user back to where they were
            // given a potentically-malicious URL, so we'll direct them to Home
            // or exit.
            <PageRoot
                template={EmbeddedPageTemplate}
                settings={{
                    title: t`Redirecting to URL from learner's webpage...`,
                }}
            >
                <View style={styles.textAlign}>
                    <p>
                        {t`A webpage created on Khan Academy wants you to visit an address that may not be safe.`}
                    </p>
                    <p>
                        {t`Please close this window, or return to Khan Academy:`}
                        <br />
                        <Link href="/">{t`Return to Khan Academy`}</Link>
                    </p>
                </View>
            </PageRoot>
        ) : (
            <PageRoot
                template={EmbeddedPageTemplate}
                settings={{
                    title: t`Redirecting to URL from learner's webpage...`,
                }}
            >
                <View style={styles.textAlign}>
                    <p>
                        {t`A webpage created on Khan Academy wants you to visit:`}
                        <br />
                        <Link href={url}>{url}</Link>
                    </p>
                    {decodedUrl !== url && (
                        <p>
                            {t`It looks like this link contains unicode characters and can also be represented as:`}
                            <br />
                            <Link href={decodedUrl}>{decodedUrl}</Link>
                        </p>
                    )}
                    <p>
                        {t`Please click the link if you'd like to go there, otherwise close this window.`}
                    </p>
                </View>
            </PageRoot>
        );
    }
}

const styles = StyleSheet.create({
    textAlign: {
        textAlign: "center",
    },
});

type ExportProps = WithoutLocation<
    JSX.LibraryManagedAttributes<
        typeof LinkRedirector,
        React.ComponentProps<typeof LinkRedirector>
    >
>;

export default compose(
    withStrings,
    withLocation,
)(LinkRedirector) as React.ComponentType<ExportProps>;
